/* @flow */
import { computed, observable, when, action, toJS, autorun } from 'mobx';
import 'event-source-polyfill';
import uuidv4 from 'uuid/v4';

import { ApiModel } from 'api/ApiModel';
import { replaceParamsInString } from 'utils/NotificationUtils';
import { authenticationStore, errorHandlerService } from 'services/ServiceRegistry';
import { filterNotificationsBySearch } from 'utils/NotificationUtils';
import { serialiseParamArray } from 'utils/compare';
import { convertToSearchString } from 'utils/uri';
import type { BasicParamItem } from 'utils/uri';

const DEFAULT_PERIOD = 25000;
const DEFAULT_PAGE_SIZE = 20;

const splitByChunkSize = (array: any[], chunk_size: number) =>
  Array(Math.ceil(array.length / chunk_size))
    .fill()
    .map((_, index) => index * chunk_size)
    .map(begin => array.slice(begin, begin + chunk_size));

export type ActionableNotificationResponse = {
  message: string,
};

export type NotificationType = 'notifications' | 'sent-requests' | 'actionable';
export type NotificationPollControllerParameters = {
  type: NotificationType,
  urlParams: BasicParamItem[],
  searchParams: BasicParamItem[],
  /**
   * This is the period between the end of the last poll, and the beginning of the next
   */
  period?: number,
  /**
   * Consider the page size carefully. If multiple subscriptions are made with multiple page sizes, this will result in multiple polling controllers, and therefore duplicate requests
   */
  pageSize?: number,
};

type PerformFetchPageDecription = {
  pageNumber: number,
  pageSize: number,
  originalPageNumbers: number[],
};
type PollControllerInternalOptions = {
  getSubscriptions: (controllerId: string) => Subscription[],
  /**
   * Takes parameters and current data, and fetches new data, returning desired data result
   */
  performFetch: (
    controller: PollController,
    pages: PerformFetchPageDecription[]
  ) => Promise<{ data: any[][], pagination: { lastPage: number, total: number } }>,
};

export type SubscriptionOptions = {
  background: boolean,
};

class PollController {
  id: string = uuidv4();
  parameters: NotificationPollControllerParameters;
  internalOptions: PollControllerInternalOptions;

  enabled: boolean;
  @observable loading = false;

  @observable pagination: {
    lastPage?: number,
    total?: number,
  } = {};

  @observable data: any[][] = [];

  lastDocumentVisibility = document.visibilityState;
  timerId: ?TimeoutID;

  /**
   * First in, last out
   */
  @observable pollQueue: { resolvers: Function[], isServicing: boolean }[] = [];

  get shouldPoll() {
    if (!this.enabled) {
      return false;
    }

    const subscriptions = this.internalOptions.getSubscriptions(this.id);
    if (!subscriptions.length) {
      return false;
    }

    if (document.visibilityState === 'hidden') {
      return subscriptions.some(sub => sub.options.background);
    }

    return true;
  }

  constructor(
    parameters: NotificationPollControllerParameters,
    internalOptions: PollControllerInternalOptions,
    enabled = true
  ) {
    this.parameters = {
      ...parameters,
      period: parameters.period || DEFAULT_PERIOD,
      pageSize: parameters.pageSize || DEFAULT_PAGE_SIZE,
    };
    this.internalOptions = internalOptions;
    this.enabled = enabled;

    autorun(() => {
      const lastQueueItem = this.pollQueue[this.pollQueue.length - 1];

      if (lastQueueItem && !lastQueueItem.isServicing) {
        lastQueueItem.isServicing = true;
        this._internalPerformPollOperation().then(() => {
          for (const resolver of lastQueueItem.resolvers) {
            resolver();
          }

          this.pollQueue.pop();
        });
      }
    });

    this.initiatePoll(true);
    this._subscribeToWindowEvents();
  }

  _handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.initiatePoll(true);
    }
  };

  _subscribeToWindowEvents = () => {
    window.addEventListener('visibilitychange', this._handleVisibilityChange);
  };

  initiatePoll = (immediate?: boolean) =>
    new Promise(resolve => {
      if (immediate) {
        setImmediate(async () => {
          await this._performPollOperation();

          resolve();
        });
        return;
      }

      this._clearTimeoutIfExists();
      this.timerId = setTimeout(async () => {
        await this._performPollOperation();

        resolve();
      }, this.parameters.period);
    });

  computeRequiredPages = (): PerformFetchPageDecription[] => {
    const flattenedRequiredPages: number[] = Array.from(
      new Set(
        this.internalOptions
          .getSubscriptions(this.id)
          .map(sub => toJS(sub.pagesInView))
          .flat()
      )
    ).sort();

    return flattenedRequiredPages.reduce((_pages, pageNumber, index) => {
      if (flattenedRequiredPages[index - 1] + 1 === pageNumber) {
        const itemToUpdate = _pages[_pages.length - 1];
        itemToUpdate.originalPageNumbers.push(pageNumber);

        let newPageSize = itemToUpdate.originalPageNumbers.length * this.parameters.pageSize;
        let startItem = (itemToUpdate.originalPageNumbers[0] - 1) * this.parameters.pageSize;

        const newMod = startItem % newPageSize;
        if (newMod !== 0) {
          newPageSize += newMod;

          if (startItem % newPageSize) {
            startItem -= newMod;
          }
        }

        itemToUpdate.pageNumber = startItem / newPageSize + 1;
        itemToUpdate.pageSize = newPageSize;

        return _pages;
      }

      return [
        ..._pages,
        {
          pageNumber,
          pageSize: this.parameters.pageSize,
          originalPageNumbers: [pageNumber],
        },
      ];
    }, []);
  };

  @action
  _performPollOperation = () => {
    const handle = new Promise(resolve => {
      if (this.pollQueue.length === 0 || (this.pollQueue[0] && this.pollQueue[0].isServicing)) {
        this.pollQueue.unshift({ resolvers: [resolve], isServicing: false });
      } else {
        this.pollQueue[0].resolvers.push(resolve);
      }
    });

    return handle;
  };

  @action
  _internalPerformPollOperation = async () => {
    this._clearTimeoutIfExists();

    if (this.shouldPoll) {
      this.loading = true;

      try {
        const { data, pagination } = await this.internalOptions.performFetch(
          this,
          this.computeRequiredPages()
        );

        this.data = data;
        this.pagination = pagination;
      } catch (error) {
        errorHandlerService.handleException(error, 'performNotificationPollOperation');
      } finally {
        this.loading = false;
      }
    }

    this.initiatePoll(false);
  };

  _clearTimeoutIfExists = () => {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  };

  @action
  enablePolling = () => {
    this.enabled = true;
  };
  @action
  disablePolling = () => {
    this.enabled = false;
  };
}

export class Subscription {
  id: string = uuidv4();
  @observable controller: PollController;
  options: SubscriptionOptions;

  @observable pagesInView: number[] = [1];

  @computed get data(): any[] {
    return this.pagesInView.reduce(
      (_data, page) => [..._data, ...(this.controller.data[page - 1] || [])],
      []
    );
  }

  @computed get loading(): boolean {
    return this.controller.loading;
  }

  get nextPagesInView(): number[] {
    const current = [...this.pagesInView];
    const proposedNewPageNumber = current[current.length - 1] + 1;

    if (proposedNewPageNumber > (this.controller.pagination.lastPage || 0)) {
      return this.pagesInView;
    }

    return current.concat([proposedNewPageNumber]);
  }

  constructor(controller: PollController, options: SubscriptionOptions) {
    this.controller = controller;
    this.options = options;
  }

  @action
  setPagesInView = async (pages: number[], setAndImmediatelyLoad?: boolean) => {
    this.pagesInView = [...pages].sort();

    if (setAndImmediatelyLoad) {
      await this.controller.initiatePoll(true);
    }
  };
}

export default class NotificationStore extends ApiModel {
  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    hasSuccess: false,
    hasError: false,
    showBackDrop: false,
  };
  @observable notification = {};
  @observable pollControllers: Map<string, PollController> = new Map();
  @observable subscriptionMap: Map<string, Subscription> = new Map();

  getNotificationSubscriptions = (controllerId: string) =>
    Array.from(this.subscriptionMap.values()).filter(sub => sub.controller.id === controllerId);

  subscribeToNotifications = (
    params: NotificationPollControllerParameters,
    options: SubscriptionOptions,
    refresh?: boolean
  ): Subscription => {
    const requiredSearchParamsString = serialiseParamArray(params.searchParams);

    const matchingController = Array.from(this.pollControllers.values()).find(controller => {
      if (controller.parameters.type !== params.type) {
        return false;
      }

      if (controller.parameters.period !== (params.period || DEFAULT_PERIOD)) {
        return false;
      }

      if (controller.parameters.pageSize !== (params.pageSize || DEFAULT_PAGE_SIZE)) {
        return false;
      }

      if (serialiseParamArray(controller.parameters.searchParams) !== requiredSearchParamsString) {
        return false;
      }

      return true;
    });

    let targetController: ?PollController = matchingController;

    if (!targetController) {
      targetController = new PollController(params, {
        getSubscriptions: this.getNotificationSubscriptions,
        performFetch: async (controller, pages) => {
          const dataResult: any[][] = [];
          let total = 0;
          let lastPage = 1;

          let abort = false;

          const handleResults = (results: any[]) => {
            for (const result of results) {
              if (result) {
                const pageSize = result.meta.per_page;
                const multiplier = pageSize / controller.parameters.pageSize;

                const splitData = splitByChunkSize(result.data, controller.parameters.pageSize);

                const startingPage = result.meta.current_page;
                for (let page = startingPage; page < startingPage + multiplier; page++) {
                  dataResult[page - 1] = splitData[page - startingPage];
                }

                total = result.meta.total;
                lastPage = result.meta.last_page * multiplier;
              } else {
                abort = true;
              }
            }
          };

          switch (controller.parameters.type) {
            case 'notifications': {
              const requests = pages.map(({ pageNumber, pageSize }, index) =>
                this.getNotifications(
                  pageNumber,
                  pageSize,
                  controller.parameters.urlParams,
                  controller.parameters.searchParams
                )
              );

              const results = await Promise.all(requests);
              handleResults(results);

              break;
            }

            case 'actionable': {
              const requests = pages.map(({ pageNumber, pageSize }, index) =>
                this.getNotifications(pageNumber, pageSize, controller.parameters.urlParams, [
                  { key: undefined, values: ['Request'] },
                  ...controller.parameters.searchParams,
                ])
              );

              const results = await Promise.all(requests);
              handleResults(results);

              break;
            }

            case 'sent-requests': {
              const result = await this.getSentRequests(
                controller.parameters.urlParams,
                controller.parameters.searchParams
              );
              if (result) {
                dataResult.push(result);
                total = result.length;
              }

              break;
            }
          }

          if (abort) {
            throw new Error('Some or all of the combined poll requests failed');
          }

          return {
            data: dataResult,
            pagination: {
              total,
              lastPage,
            },
          };
        },
      });

      this.pollControllers.set(targetController.id, targetController);
    } else if (refresh) {
      targetController.initiatePoll(true);
    }

    const subscription = new Subscription(targetController, options);
    this.subscriptionMap.set(subscription.id, subscription);

    return subscription;
  };

  unsubscribeFromNotifications = (subscriptionId: string) => {
    const sub = this.subscriptionMap.get(subscriptionId);

    if (sub) {
      const { id: controllerId } = sub.controller;
      this.subscriptionMap.delete(subscriptionId);
      this.checkAndCleanController(controllerId);
    }
  };

  checkAndCleanController = (controllerId: string) => {
    const controllerSubscriptions = this.getNotificationSubscriptions(controllerId);

    if (!controllerSubscriptions.length) {
      const controller = this.pollControllers.get(controllerId);

      if (controller) {
        controller._clearTimeoutIfExists();
        this.pollControllers.delete(controllerId);
      }
    }
  };

  /**
   * Get sent requests
   */
  getSentRequests = async (queryParams?: BasicParamItem[], searchParams?: BasicParamItem[]) => {
    const _params = new URLSearchParams();
    _params.set('chart', 'sent-requests');

    if (queryParams && queryParams.length) {
      for (const param of queryParams) {
        if (param.key) {
          _params.set(param.key, param.values.join('|'));
        }
      }
    }

    if (!_params.has('id')) {
      _params.set('id', '0');
    }

    if (searchParams && searchParams.length) {
      _params.set('search', convertToSearchString(searchParams));
    }

    const paramString = Array.from(_params.keys()).length ? `?${_params.toString()}` : '';

    const url = `dashboard${paramString}`;
    const response = await this.api.getResource(url);

    return response && response['sent-requests'];
  };

  /**
   * Get one sent request
   */
  getSentRequestById = async (id: string) => {
    try {
      const url: string = `sent-requests/${id}`;
      this.notification = await this.api.getResource(url);

      return this.notification;
    } catch (err) {
      errorHandlerService.handleException(err);
      return null;
    }
  };

  getNotifications = async (
    page: number | 'all' = 'all',
    pageSize?: number,
    queryParams?: BasicParamItem[],
    searchParams?: BasicParamItem[]
  ) => {
    try {
      const _params = new URLSearchParams();
      if (page !== 'all') {
        _params.set('page', String(page));
      }

      if (typeof pageSize !== 'undefined') {
        _params.set('page_size', String(pageSize));
      }

      if (queryParams && queryParams.length) {
        for (const param of queryParams) {
          if (param.key) {
            _params.set(param.key, param.values.join('|'));
          }
        }
      }

      if (searchParams && searchParams.length) {
        _params.set('search', convertToSearchString(searchParams));
      }

      const paramString = Array.from(_params.keys()).length ? `?${_params.toString()}` : '';

      const url = `notifications${paramString}`;
      const response = await this.api.getResource(url);

      return response;
    } catch (err) {
      errorHandlerService.handleException(err, 'getAllNotifications');
    }
  };

  /**
   * Get Notification by ID
   */
  getNotificationById = async (id: string) => {
    try {
      const url = `notifications/${id}`;
      this.notification = await this.api.getResource(url);

      return this.notification;
    } catch (err) {
      errorHandlerService.handleException(err);
      return null;
    }
  };

  reloadAllNotifications = async (type?: NotificationType) => {
    const targetControllers = type
      ? Array.from(this.pollControllers.values()).filter(
          controller => controller.parameters.type === type
        )
      : Array.from(this.pollControllers.values());

    const promises = [];

    for (const targetController of targetControllers) {
      promises.push(targetController.initiatePoll(true));
    }

    return Promise.all(promises);
  };

  /**
   * Handle action
   */
  handleAction = async (
    act: Object,
    templateValues: Object,
    formData?: Object,
    onSuccess?: Function
  ) => {
    const endpoint = replaceParamsInString(act.endpoint, templateValues);
    const obj = formData || act.fields;

    switch (act.request_type) {
      case 'PUT':
        try {
          const response = await this.api.putResource(endpoint, obj, {
            fullResponse: true,
          });

          await this.reloadAllNotifications('actionable');

          if (onSuccess) {
            onSuccess(response);
          }

          this.showResponseToastMessage(true, response);
        } catch (err) {
          errorHandlerService.handleException(err);

          this.showResponseToastMessage(false, err);
        }

        break;
      case 'POST':
        try {
          const response = await this.api.createResource(endpoint, obj, {
            fullResponse: true,
          });

          await this.reloadAllNotifications('actionable');

          if (onSuccess) {
            onSuccess(response);
          }

          this.showResponseToastMessage(true, response);
        } catch (err) {
          errorHandlerService.handleException(err);

          this.showResponseToastMessage(false, err);
        }

        break;
      default:
        break;
    }

    await Promise.all([
      this.reloadAllNotifications('sent-requests'),
      this.reloadAllNotifications('notifications'),
    ]);
  };

  showResponseToastMessage = (success: boolean, response: ActionableNotificationResponse) => {
    this.hideLoadingToastMessage();

    this.localState.responseMessage = response.message;
    this.localState.showResponseMessage = true;
    this.localState.hasError = !success;
    this.localState.hasSuccess = success;

    setTimeout(() => {
      this.localState.showResponseMessage = false;
      this.localState.hasError = false;
      this.localState.hasSuccess = false;
      this.localState.isLoading = false;
    }, 2000);
  };

  showLoadingToastMessage = () => {
    this.localState.responseMessage = t => t('loading_states.refreshing');
    this.localState.showResponseMessage = true;
    this.localState.hasError = false;
    this.localState.hasSuccess = false;
    this.localState.isLoading = true;
  };

  hideLoadingToastMessage = () => {
    this.localState.showResponseMessage = false;
    this.localState.hasError = false;
    this.localState.hasSuccess = false;
    this.localState.isLoading = false;
  };
}
